import { sha256Hex } from './sha256-hex';
import { normalizeTechnologyName } from './technology-name';
import { z } from 'zod';

export const MISCONFIG_RESEARCH_RUN_STATUSES = [
  'PENDING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'APPROVED',
] as const;

export const MisconfigResearchRunStatusSchema = z.enum(
  MISCONFIG_RESEARCH_RUN_STATUSES,
);

export type MisconfigResearchRunStatus =
  (typeof MISCONFIG_RESEARCH_RUN_STATUSES)[number];

export const SIGNAL_ELIGIBLE_RUN_STATUSES = [
  'COMPLETED',
  'APPROVED',
] as const satisfies readonly MisconfigResearchRunStatus[];

export type SignalEligibleRunStatus =
  (typeof SIGNAL_ELIGIBLE_RUN_STATUSES)[number];

export const isSignalEligibleRunStatus = (
  status: string,
): status is SignalEligibleRunStatus =>
  SIGNAL_ELIGIBLE_RUN_STATUSES.some((eligible) => eligible === status);

export const MisconfigurationSettingSchema = z.object({
  setting: z.string().min(1),
  location: z.string().min(1),
  mechanism: z.string().min(1),
  vulnerableValue: z.string().min(1),
  secureValue: z.string().min(1),
  shippedDefault: z.string().min(1),
  isProductDefault: z.boolean(),
  role: z.string().min(1),
});

export const MISCONFIG_CATALOG_TYPES = [
  'misconfiguration',
  'risky-feature',
] as const;
export const MisconfigCatalogTypeSchema = z.enum(MISCONFIG_CATALOG_TYPES);
export type MisconfigCatalogType = (typeof MISCONFIG_CATALOG_TYPES)[number];

export const MISCONFIG_TARGET_TYPES = ['web', 'external', 'internal'] as const;
export const MisconfigTargetTypeSchema = z.enum(MISCONFIG_TARGET_TYPES);
export type MisconfigTargetType = (typeof MISCONFIG_TARGET_TYPES)[number];

export const normalizeMisconfigTargetTypes = (
  values: readonly MisconfigTargetType[],
): MisconfigTargetType[] =>
  MISCONFIG_TARGET_TYPES.filter((type) => values.includes(type));

export const CAPABILITY_CLASSES = [
  'os-command-execution',
  'code-or-script-evaluation',
  'arbitrary-file-read-or-write',
  'server-side-request-or-network-pivot',
  'credential-or-secret-retrieval',
  'identity-assumption-or-impersonation',
  'authentication-or-authorization-bypass-by-design',
  'configuration-or-code-deployment',
  'audit-or-logging-suppression',
  'bulk-data-read-or-export',
] as const;
export const CapabilityClassSchema = z.enum(CAPABILITY_CLASSES);
export type CapabilityClass = (typeof CAPABILITY_CLASSES)[number];

export const REACHED_VIA_KINDS = [
  'network-listener',
  'authenticated-api-or-console',
  'local-ipc-or-socket',
  'scheduled-or-event-trigger',
  'embedded-in-integrator-code',
] as const;
export const ReachedViaKindSchema = z.enum(REACHED_VIA_KINDS);
export type ReachedViaKind = (typeof REACHED_VIA_KINDS)[number];

export const REQUIRED_PRIVILEGES = [
  'unauthenticated',
  'any-authenticated-user',
  'low-privilege-role',
  'admin-or-equivalent',
  'local-host-only',
] as const;
export const RequiredPrivilegeSchema = z.enum(REQUIRED_PRIVILEGES);
export type RequiredPrivilege = (typeof REQUIRED_PRIVILEGES)[number];

export const RISKY_FEATURE_GATINGS = [
  'disable-switch',
  'restrict-or-allow-list',
  'privilege-separation',
  'none',
] as const;
export const RiskyFeatureGatingSchema = z.enum(RISKY_FEATURE_GATINGS);
export type RiskyFeatureGating = (typeof RISKY_FEATURE_GATINGS)[number];

export const RiskyFeatureSchema = z.object({
  capability: z.string().min(1),
  capabilityClass: CapabilityClassSchema,
  reachedVia: z.string().min(1),
  reachedViaKind: ReachedViaKindSchema,
  requiredPrivilege: RequiredPrivilegeSchema,
  gating: RiskyFeatureGatingSchema,
  escalationDelta: z.string().optional(),
});

export const VERSION_APPLICABILITIES = [
  'confirmed',
  'inferred',
  'unknown',
] as const;
export const VersionApplicabilitySchema = z.enum(VERSION_APPLICABILITIES);
export type VersionApplicability = (typeof VERSION_APPLICABILITIES)[number];

export const CONFIDENCE_LEVELS = ['high', 'medium', 'low'] as const;
export const ConfidenceLevelSchema = z.enum(CONFIDENCE_LEVELS);
export type ConfidenceLevel = (typeof CONFIDENCE_LEVELS)[number];

export const FindingMetadataSchema = z
  .object({
    versionApplicability: VersionApplicabilitySchema.optional(),
    confidence: ConfidenceLevelSchema.optional(),
    areaId: z.string().min(1).optional(),
    areaFeature: z.string().min(1).optional(),
    areaKey: z.string().min(1).optional(),
    areaLabel: z.string().min(1).optional(),
    mergedFromIds: z.array(z.string().min(1)).optional(),
    mergedSources: z.array(z.string().min(1)).optional(),
    signalDescription: z.string().min(1).optional(),
    signalAnalysis: z.string().min(1).optional(),
    signalReason: z.string().min(1).optional(),
    signalHypothesis: z.string().min(1).optional(),
  })
  .passthrough();

export const FindingEntrySchema = z
  .object({
    vulnerableState: z.string().min(1),
    misconfigurationSet: z.array(MisconfigurationSettingSchema).default([]),
    combinationLogic: z.string().default('all'),
    description: z.string().min(1),
    technicalExplanation: z.string().min(1),
    validationSteps: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
    source: z.string().optional(),
    conditions: z.string().optional(),
    prerequisites: z.array(z.string()).default([]),
    technologyName: z.string().min(1),
    potentialImpact: z.string().optional(),
    metadata: FindingMetadataSchema.optional(),
    configurationRootCause: z.string().optional(),
    versionRange: z.string().min(1),
    date: z.string().optional(),
    sourceVerified: z.string().optional(),
    findingType: MisconfigCatalogTypeSchema.optional(),
    riskyFeature: RiskyFeatureSchema.optional(),
    targetTypes: z.array(MisconfigTargetTypeSchema).default([]),
  })
  .superRefine((finding, ctx) => {
    const isRisky = finding.findingType === 'risky-feature';

    if (isRisky && !finding.riskyFeature) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['riskyFeature'],
        message:
          "findingType is 'risky-feature' but no riskyFeature block was provided.",
      });
    }

    if (!isRisky && finding.riskyFeature) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['findingType'],
        message:
          "riskyFeature was provided but findingType is not 'risky-feature'.",
      });
    }

    if (!isRisky && finding.misconfigurationSet.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['misconfigurationSet'],
        message:
          'A misconfiguration finding needs a non-empty misconfigurationSet.',
      });
    }

    if (
      isRisky &&
      finding.riskyFeature &&
      finding.riskyFeature.gating !== 'none' &&
      finding.misconfigurationSet.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['misconfigurationSet'],
        message:
          'riskyFeature.gating claims a switch exists; misconfigurationSet must name it.',
      });
    }
  });

export const FindingsFileSchema = z.array(FindingEntrySchema).min(1);

export type MisconfigurationSetting = z.infer<
  typeof MisconfigurationSettingSchema
>;
export type RiskyFeature = z.infer<typeof RiskyFeatureSchema>;
export type FindingMetadata = z.infer<typeof FindingMetadataSchema>;
export type FindingEntry = z.infer<typeof FindingEntrySchema>;

export const isRiskyFeatureFinding = (
  finding: Pick<FindingEntry, 'riskyFeature'>,
): boolean => finding.riskyFeature !== undefined;

export const MISCONFIG_SIGNAL_ELIGIBLE_TARGET_TYPES: readonly MisconfigTargetType[] =
  ['web'];

export const isMisconfigFindingSignalEligible = (
  finding: Pick<FindingEntry, 'targetTypes'>,
): boolean =>
  finding.targetTypes.some((targetType) =>
    MISCONFIG_SIGNAL_ELIGIBLE_TARGET_TYPES.includes(targetType),
  );

export const computeFindingEntryId = (entry: FindingEntry): string =>
  sha256Hex(
    [
      entry.technologyName.trim().toLowerCase(),
      entry.versionRange.trim(),
      entry.vulnerableState.trim(),
    ].join('::'),
  ).slice(0, 32);

export const parseFindingsFile = (raw: unknown): FindingEntry[] =>
  FindingsFileSchema.parse(raw);

export const MISCONFIG_CREATED_SIGNALS_STATUSES = [
  'new',
  'updated',
  'skipped',
] as const;

export const MisconfigCreatedSignalsStatusSchema = z.enum(
  MISCONFIG_CREATED_SIGNALS_STATUSES,
);

export type MisconfigCreatedSignalsStatus =
  (typeof MISCONFIG_CREATED_SIGNALS_STATUSES)[number];

export const MisconfigCreatedSignalsEntrySchema = z.object({
  applicationId: z.string().min(1),
  applicationName: z.string().nullable(),
  tenantId: z.string().min(1),
  recordCount: z.number().int().nonnegative(),
  endpointIds: z.array(z.string()),
  createdAt: z.string(),
  createdBy: z.string().min(1),
  creationCount: z.number().int().positive(),
});

export const MisconfigCreatedSignalsEntryListSchema = z.array(
  MisconfigCreatedSignalsEntrySchema,
);

export type MisconfigCreatedSignalsEntry = z.infer<
  typeof MisconfigCreatedSignalsEntrySchema
>;

export const parseMisconfigCreatedSignals = (
  raw: unknown,
): MisconfigCreatedSignalsEntry[] => {
  const parsed = parseMisconfigCreatedSignalsResult(raw);

  return parsed.data;
};

export const parseMisconfigCreatedSignalsResult = (
  raw: unknown,
): {
  data: MisconfigCreatedSignalsEntry[];
  valid: boolean;
} => {
  if (raw === null || raw === undefined) {
    return { data: [], valid: true };
  }

  const parsed = MisconfigCreatedSignalsEntryListSchema.safeParse(raw);

  return parsed.success
    ? { data: parsed.data, valid: true }
    : { data: [], valid: false };
};

export const MisconfigResearchRequestedPayloadSchema = z.object({
  runId: z.string().min(1),
  technologyName: z.string().min(1),
  versionRange: z.string().min(1),
  customPrompt: z.string().min(1).optional(),
});

export const MisconfigResearchCompletedPayloadSchema = z.object({
  runId: z.string().min(1),
  findings: z.unknown().nullable(),
  error: z.string().min(1).optional(),
});

export const buildMisconfigTechnologyKey = ({
  technologyName,
  versionRange,
}: {
  technologyName: string;
  versionRange: string;
}): string =>
  `${normalizeTechnologyName(technologyName)}:${versionRange.trim()}`;
